import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../core/hooks';
import { useWidgetId } from '../../core/IdProvider';
import { SellingMode } from '@src/core/buyerApiClient';
import {
  currentProductAdderStatus,
  productAdderStepState,
  productScheduleSelector,
  productSelector,
  selectAvailableDate,
  tariffsState,
} from '../../features/product';
import SpinnerBar from '../reusable/spinner/SpinnerBar';
import ProductAdderAdditionals from './ProductAdderAdditionals';
import ProductAdderSeatMap from './ProductAdderSeatMap';
import ProductAdderStatus from './ProductAdderStatus';
import ProductAdderTariff from './ProductAdderTariff';
import ProductAdderCalendar from './ProductAdderCalendar';
import { device } from '@src/common/breakpoints';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import usePackageIdParam from '@src/hooks/usePackageIdParam';

const ProductAdderBody = () => {
  const widgetId = useWidgetId();
  const productId = useSelectedProductId();
  const packageId = usePackageIdParam();
  const { selectedDate } = useAppSelector(tariffsState(widgetId));
  const product = useAppSelector(productSelector(productId));
  const dispatch = useAppDispatch();
  const { currentStep } = useAppSelector(productAdderStepState(widgetId));
  const currentAdderStatus = useAppSelector(currentProductAdderStatus(widgetId));
  const { defaultTariffGroup } = useAppSelector(productScheduleSelector(productId));
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (product?.sellingMode === SellingMode.WithoutDate && !selectedDate && defaultTariffGroup) {
      dispatch(selectAvailableDate({ id: widgetId, value: null }));
    }
  }, [product, selectedDate, defaultTariffGroup]);

  useEffect(() => {
    if (currentStep && currentStep !== 'calendar') {
      rootRef.current?.scrollIntoView();
    }
  }, [currentStep]);

  return (
    <ProductBody ref={rootRef}>
      {currentStep === 'calendar' && <ProductAdderCalendar />}
      {currentStep === 'tariff' && <ProductAdderTariff />}
      {currentStep === 'seat_map' && <ProductAdderSeatMap />}
      {currentStep === 'additional' && <ProductAdderAdditionals />}
      {currentStep === 'finish' && <ProductAdderStatus />}
      {currentAdderStatus === 'adding_to_cart' ? (
        <SpinnerBarArea showBackdrop={!packageId}>
          <SpinnerBar />
        </SpinnerBarArea>
      ) : null}
    </ProductBody>
  );
};

const ProductBody = styled.div`
  padding: 8px;
  position: relative;
  min-height: 100px;

  @media (min-width: 375px) {
    padding: 16px;
  }

  ${device.smallTablet} {
    padding: 24px;
  }
`;

interface SpinnerBarAreaProps {
  showBackdrop: boolean;
}
const SpinnerBarArea = styled.div<SpinnerBarAreaProps>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) =>
    props.showBackdrop
      ? css`
          opacity: 0.7;
          background-color: #000000;
        `
      : ''}
`;
export default ProductAdderBody;
